import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Grid,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery } from '@tanstack/react-query';
import Page from 'src/components/Page';
import { useAuth } from 'src/context/auth/AuthContext';
import { studentService } from 'src/services/student.service';
import { formatMoney, payrollImportService } from 'src/services/payroll';
import type { PayrollSnapshot } from 'src/types/payroll.types';
import { PaycheckStub } from 'src/pages/Admin/payroll/components/PaycheckStub';

export default function StudentPaychecks() {
  const { user } = useAuth();
  const [selected, setSelected] = useState<{
    snapshot: PayrollSnapshot;
    amount: number;
    createdAt: string;
  } | null>(null);

  const { data: studentId } = useQuery({
    queryKey: ['student', user?.id],
    queryFn: () => studentService.getStudentByUserId(user?.id as string),
    enabled: !!user?.id,
  });

  const { data: paychecks, isLoading } = useQuery({
    queryKey: ['student-paychecks', studentId],
    queryFn: () => payrollImportService.listStudentPaychecks(studentId as string),
    enabled: !!studentId,
  });

  const studentName = user?.dbUser ? `${user.dbUser.firstname || ''} ${user.dbUser.lastname || ''}`.trim() : '';

  return (
    <Page title="My Paychecks">
      <Container
        maxWidth={false}
        sx={{ width: '100%', px: { xs: 1.5, sm: 2.5, lg: 3 }, mx: 0 }}
      >
        <Stack spacing={0.5} sx={{ mb: 2.25 }}>
          <Typography variant="h4" fontWeight={800}>
            My paychecks
          </Typography>
          <Typography color="text.secondary">
            Scholar Dollar pay stubs from grade-based payroll. Tax withheld is saved for Tax Day — only net hit your
            wallet.
          </Typography>
        </Stack>

        {isLoading ? (
          <Box sx={{ py: 10, textAlign: 'center' }}>
            <CircularProgress />
          </Box>
        ) : !paychecks?.length ? (
          <Card
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4 },
              textAlign: 'center',
              borderRadius: 3,
              border: '1px dashed',
              borderColor: 'divider',
            }}
          >
            <Icon icon="mdi:file-document-outline" width={48} color="#A0AEC0" />
            <Typography sx={{ mt: 1.5 }} color="text.secondary">
              No paychecks yet. When your school runs payroll, stubs will show up here.
            </Typography>
          </Card>
        ) : (
          <Grid container spacing={2}>
            {paychecks.map((tx: any) => {
              const payroll = tx.extendedproperties?.payroll as PayrollSnapshot | undefined;
              return (
                <Grid item xs={12} key={tx.id}>
                  <Card
                    elevation={0}
                    sx={{
                      p: { xs: 1.75, sm: 2.25 },
                      borderRadius: 3,
                      border: '1px solid #E2E8F0',
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      alignItems: { sm: 'center' },
                      justifyContent: 'space-between',
                      gap: 2,
                      transition: 'box-shadow 0.15s ease',
                      '&:hover': {
                        boxShadow: `0 8px 24px ${alpha('#297041', 0.1)}`,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: 2,
                          bgcolor: alpha('#EDB42E', 0.15),
                          display: 'grid',
                          placeItems: 'center',
                          color: '#C48F12',
                        }}
                      >
                        <Icon icon="mdi:cash-check" width={26} />
                      </Box>
                      <Box>
                        <Typography fontWeight={700}>
                          {payroll?.pay_period_key
                            ? payroll.pay_period_key.replace('_', ' → ')
                            : 'Scholar Dollar Paycheck'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {new Date(tx.createdat).toLocaleDateString()}
                          {payroll
                            ? ` · Gross ${formatMoney(payroll.gross)} · Tax ${formatMoney(payroll.tax_withheld)}`
                            : ''}
                        </Typography>
                      </Box>
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="h6" fontWeight={800} color="#297041">
                        +{formatMoney(Number(tx.amount))}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={!payroll}
                        sx={{ textTransform: 'none', borderRadius: 2, fontWeight: 600 }}
                        onClick={() => {
                          if (!payroll) return;
                          setSelected({
                            snapshot: payroll,
                            amount: Number(tx.amount),
                            createdAt: tx.createdat,
                          });
                        }}
                      >
                        View stub
                      </Button>
                    </Stack>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        )}

        <Dialog
          open={!!selected}
          onClose={() => setSelected(null)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
        >
          {selected && (
            <PaycheckStub
              snapshot={selected.snapshot}
              studentName={studentName}
              transactionAmount={selected.amount}
              createdAt={selected.createdAt}
              onClose={() => setSelected(null)}
            />
          )}
        </Dialog>
      </Container>
    </Page>
  );
}
